import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, X, Play, LockKeyhole, Wallet, Ban, Plus, Trash2, Check,
  Download, ChevronDown,
} from "lucide-react";

const STATUS_COLORS = {
  draft: "text-foreground/60 border-foreground/20",
  preparing: "text-amber-500 border-amber-500/40",
  finalized: "text-emerald-500 border-emerald-500/40",
  paid: "text-foreground/40 border-foreground/15",
  cancelled: "text-red-500 border-red-500/40",
};

const LINE_TYPES = [
  { k: "overtime",   label: "Overtime",   sign: "+" },
  { k: "tips",       label: "Tips",       sign: "+" },
  { k: "commission", label: "Commission", sign: "+" },
  { k: "bonus",      label: "Bonus",      sign: "+" },
  { k: "advance",    label: "Advance",    sign: "-" },
  { k: "deduction",  label: "Deduction",  sign: "-" },
  { k: "tax",        label: "Tax",        sign: "-" },
];

const POSITIVE = new Set(["base","overtime","tips","commission","bonus"]);

export default function PayrollPeriodDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm } = useAuth();

  const [period, setPeriod] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { slip, manual_lines: [{type,label,amount,notes}] }
  const [csvFormats, setCsvFormats] = useState([]);
  const [csvOpen, setCsvOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/pay-periods/${id}`);
      setPeriod(data); setPayslips(data.payslips || []); setTotals(data.totals);
    } catch (_) { toast.error("Could not load period"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payroll/csv-formats");
        setCsvFormats(data);
      } catch (_) {}
    })();
  }, []);

  const doExportCsv = async (formatKey) => {
    setDownloading(true);
    setCsvOpen(false);
    try {
      const res = await api.get(`/pay-periods/${id}/export.csv`, {
        params: { format: formatKey },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (period?.name || "payroll").replace(/\s+/g, "_");
      link.download = `payroll_${safeName}_${formatKey}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${formatKey.toUpperCase()} CSV`);
    } catch (e) {
      // For blob errors we need to parse the response text
      let msg = "Export failed";
      try {
        const txt = await e.response?.data?.text?.();
        if (txt) msg = JSON.parse(txt).detail || msg;
      } catch (_) {}
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const doPrepare = async () => {
    try {
      const { data } = await api.post(`/pay-periods/${id}/prepare`);
      toast.success(`Prepared ${data.payslips_created} payslip${data.payslips_created === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doFinalize = async () => {
    if (!window.confirm("Finalize this period? Payslips will be locked.")) return;
    try {
      await api.post(`/pay-periods/${id}/finalize`);
      toast.success("Period finalized");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doMarkPaid = async () => {
    if (!window.confirm("Mark this period as paid?")) return;
    try {
      await api.post(`/pay-periods/${id}/mark-paid`);
      toast.success("Marked paid");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doCancel = async () => {
    if (!window.confirm("Cancel this period? Draft payslips will be discarded.")) return;
    try {
      await api.post(`/pay-periods/${id}/cancel`);
      toast.success("Period cancelled"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEditor = (slip) => {
    // Manual lines are those without system:true.
    const manual = (slip.lines || [])
      .filter((l) => !l.system)
      .map((l) => ({ type: l.type, label: l.label, amount: l.amount, notes: l.notes || "" }));
    setEditing({ slip, manual_lines: manual });
  };

  const addLine = () => {
    setEditing({
      ...editing,
      manual_lines: [...editing.manual_lines, { type: "tips", label: "", amount: 0, notes: "" }],
    });
  };
  const updateLine = (idx, patch) => {
    const l = [...editing.manual_lines];
    l[idx] = { ...l[idx], ...patch };
    setEditing({ ...editing, manual_lines: l });
  };
  const removeLine = (idx) => {
    const l = [...editing.manual_lines];
    l.splice(idx, 1);
    setEditing({ ...editing, manual_lines: l });
  };
  const saveLines = async () => {
    try {
      const payload = editing.manual_lines
        .filter((l) => l.label.trim() && l.amount > 0)
        .map((l) => ({ type: l.type, label: l.label.trim(),
                          amount: parseFloat(l.amount), notes: l.notes }));
      await api.patch(`/payslips/${editing.slip.id}`, { lines: payload });
      toast.success("Payslip updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="p-14 ink-subtle text-sm">Loading period…</div>;
  if (!period)  return <div className="p-14 ink-subtle text-sm">Period not found.</div>;

  const canEdit = hasPerm("payroll.prepare") && period.status !== "finalized" && period.status !== "paid" && period.status !== "cancelled";
  const canPrepare = canEdit;
  const canFinalize = hasPerm("payroll.prepare") && (period.status === "draft" || period.status === "preparing") && payslips.length > 0;
  const canMarkPaid = hasPerm("payroll.prepare") && period.status === "finalized";
  const canCancel = hasPerm("payroll.prepare") && ["draft","preparing"].includes(period.status);
  const canExport = hasPerm("payroll.prepare") && ["finalized", "paid"].includes(period.status) && payslips.length > 0;

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="payroll-period-detail">
      <button onClick={() => nav("/payroll/periods")} data-testid="period-back-btn"
              className="tiny-label ink-subtle hover:ink flex items-center gap-1">
        <ArrowLeft size={12}/> All pay periods
      </button>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="flex items-center gap-3">
            <span className={`text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[period.status]}`} data-testid="period-status">
              {period.status}
            </span>
            <span className="tiny-label ink-subtle mono">{period.starts_on} → {period.ends_on}</span>
          </div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">{period.name}</h1>
          {period.notes && <p className="mt-2 ink-muted text-sm max-w-lg">{period.notes}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canPrepare && <button onClick={doPrepare} data-testid="period-prepare-btn" className="btn-pill btn-pill-outline h-10"><Play size={13}/> Prepare</button>}
          {canFinalize && <button onClick={doFinalize} data-testid="period-finalize-btn" className="btn-pill btn-pill-solid h-10"><LockKeyhole size={13}/> Finalize</button>}
          {canMarkPaid && <button onClick={doMarkPaid} data-testid="period-mark-paid-btn" className="btn-pill btn-pill-solid h-10"><Wallet size={13}/> Mark paid</button>}
          {canExport && (
            <div className="relative">
              <button onClick={() => setCsvOpen((o) => !o)} disabled={downloading}
                      data-testid="period-export-btn"
                      className="btn-pill btn-pill-outline h-10 border-indigo-500/40 text-indigo-500 hover:bg-indigo-500/5">
                <Download size={13}/> {downloading ? "Exporting…" : "Export CSV"} <ChevronDown size={12}/>
              </button>
              {csvOpen && (
                <div className="absolute right-0 top-11 z-30 w-64 surface-2 border hairline rounded-md shadow-2xl overflow-hidden" data-testid="period-export-menu">
                  {csvFormats.map((f) => (
                    <button key={f.key} onClick={() => doExportCsv(f.key)}
                            data-testid={`period-export-${f.key}`}
                            className="w-full text-left px-4 py-3 hover:bg-foreground/5 border-b hairline-soft last:border-b-0">
                      <div className="text-[11px] uppercase tracking-[0.22em] font-bold ink">{f.label}</div>
                      <div className="mt-1 text-[10px] ink-subtle mono">{f.columns.slice(0, 5).join(" · ")}{f.columns.length > 5 ? " …" : ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {canCancel && <button onClick={doCancel} data-testid="period-cancel-btn" className="btn-pill btn-pill-outline h-10 text-red-500 border-red-500/40 hover:bg-red-500/5"><Ban size={13}/> Cancel</button>}
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
        <Kpi num=".01" label="Employees"   value={totals?.employees ?? 0}/>
        <Kpi num=".02" label="Gross pay"   value={peso(totals?.gross_pay ?? 0)}/>
        <Kpi num=".03" label="Deductions"  value={peso(totals?.deductions_total ?? 0)}/>
        <Kpi num=".04" label="Net payable" value={peso(totals?.net_pay ?? 0)} accent/>
      </div>

      {/* Empty state */}
      {payslips.length === 0 && (
        <div className="mt-14 border hairline-soft rounded-md p-12 text-center animate-fade-up animate-fade-up-2" data-testid="period-empty">
          <Wallet size={40} className="mx-auto ink" strokeWidth={1.2}/>
          <div className="mt-5 display-heading text-2xl ink">No payslips yet.</div>
          <div className="mt-2 ink-subtle text-sm max-w-sm mx-auto">Prepare snapshots hours from the timesheet endpoint + commissions from any events closed inside this range.</div>
        </div>
      )}

      {/* Payslip table */}
      {payslips.length > 0 && (
        <div className="mt-14 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="payslip-table">
          <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
            <div className="col-span-3">Employee</div>
            <div className="col-span-2">Pay type</div>
            <div className="col-span-1 text-right">Lines</div>
            <div className="col-span-2 text-right">Gross</div>
            <div className="col-span-2 text-right">Deductions</div>
            <div className="col-span-2 text-right">Net</div>
          </div>
          {payslips.map((s) => (
            <button key={s.id} onClick={() => canEdit && openEditor(s)} disabled={!canEdit}
                    data-testid={`payslip-row-${s.id}`}
                    className={`w-full text-left grid grid-cols-12 py-3 items-center transition-colors ${canEdit ? "hover:bg-foreground/[0.03] cursor-pointer" : "cursor-default"}`}>
              <div className="col-span-3 pr-2 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] uppercase tracking-widest font-bold shrink-0">
                  {s.employee_snapshot.name.split(" ").map((n)=>n[0]).slice(0,2).join("")}
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{s.employee_snapshot.name}</div>
                  <div className="text-xs ink-subtle mono">{s.employee_snapshot.code} · {s.employee_snapshot.position}</div>
                </div>
              </div>
              <div className="col-span-2 tiny-label">
                {s.employee_snapshot.pay_type === "hourly" ? `${peso(s.employee_snapshot.pay_rate)}/hr`
                  : s.employee_snapshot.pay_type === "per_shift" ? `${peso(s.employee_snapshot.pay_rate)}/shift`
                  : `${peso(s.employee_snapshot.pay_rate)}/mo`}
              </div>
              <div className="col-span-1 text-right mono text-sm ink-muted">{s.lines.length}</div>
              <div className="col-span-2 text-right mono text-sm ink">{peso(s.gross_pay)}</div>
              <div className="col-span-2 text-right mono text-sm text-red-500">{s.deductions_total > 0 ? `−${peso(s.deductions_total)}` : "—"}</div>
              <div className="col-span-2 text-right mono text-sm ink font-semibold">{peso(s.net_pay)}</div>
            </button>
          ))}
        </div>
      )}

      {/* Line editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="payslip-editor">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)}/>
          <div className="relative w-full max-w-lg surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Payroll · Adjust payslip</div>
                <h2 className="mt-2 display-heading text-2xl ink">{editing.slip.employee_snapshot.name}</h2>
                <p className="mt-2 ink-muted text-xs">System lines (base pay + commissions) are locked. Add manual adjustments below — positive types add to gross, negative types deduct.</p>
              </div>
              <button onClick={() => setEditing(null)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            {/* System lines */}
            <div className="mt-6">
              <div className="tiny-label ink-subtle mb-2">System-computed</div>
              <div className="border hairline-soft rounded-md divide-y hairline-soft" data-testid="payslip-system-lines">
                {(editing.slip.lines || []).filter((l) => l.system).map((l, i) => (
                  <div key={i} className="p-3 flex items-center justify-between text-xs">
                    <div className="min-w-0 pr-3">
                      <div className="ink font-semibold uppercase tracking-[0.2em] text-[10px]">{l.type}</div>
                      <div className="ink-subtle truncate mt-0.5">{l.label}</div>
                    </div>
                    <div className={`mono font-semibold text-sm ${POSITIVE.has(l.type) ? "ink" : "text-red-500"}`}>
                      {POSITIVE.has(l.type) ? "+" : "−"}{peso(Math.abs(l.amount))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Manual lines */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="tiny-label ink-subtle">Manual adjustments</div>
                <button onClick={addLine} data-testid="payslip-add-line" className="tiny-label ink hover:underline flex items-center gap-1"><Plus size={11}/> Add line</button>
              </div>
              {editing.manual_lines.length === 0 && (
                <div className="border hairline-soft rounded-md p-4 text-center ink-subtle text-xs">No manual adjustments yet.</div>
              )}
              <div className="space-y-2" data-testid="payslip-manual-lines">
                {editing.manual_lines.map((l, i) => {
                  const meta = LINE_TYPES.find((t) => t.k === l.type);
                  return (
                    <div key={i} className="border hairline surface-card rounded-md p-3 space-y-2" data-testid={`payslip-line-${i}`}>
                      <div className="flex items-center gap-2">
                        <select value={l.type} onChange={(e) => updateLine(i, { type: e.target.value })}
                                className="h-9 rounded-md border hairline surface-2 px-2 text-xs ink font-semibold uppercase tracking-[0.2em]"
                                data-testid={`payslip-line-type-${i}`}>
                          {LINE_TYPES.map((t) => <option key={t.k} value={t.k}>{t.sign} {t.label}</option>)}
                        </select>
                        <input type="number" step="0.01" value={l.amount}
                               onChange={(e) => updateLine(i, { amount: e.target.value })}
                               className="flex-1 h-9 rounded-md border hairline surface-2 px-2 mono ink text-right"
                               data-testid={`payslip-line-amount-${i}`} placeholder="0"/>
                        <button onClick={() => removeLine(i)} data-testid={`payslip-line-remove-${i}`}
                                className="text-red-500/70 hover:text-red-500 p-1"><Trash2 size={13}/></button>
                      </div>
                      <input value={l.label} onChange={(e) => updateLine(i, { label: e.target.value })}
                             placeholder="Label (e.g. Tips for July 10)"
                             className="w-full h-9 rounded-md border hairline surface-2 px-2 text-xs ink"
                             data-testid={`payslip-line-label-${i}`}/>
                      <input value={l.notes} onChange={(e) => updateLine(i, { notes: e.target.value })}
                             placeholder="Notes (optional)"
                             className="w-full h-9 rounded-md border hairline surface-2 px-2 text-xs ink"
                             data-testid={`payslip-line-notes-${i}`}/>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setEditing(null)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button onClick={saveLines} data-testid="payslip-save" className="btn-pill btn-pill-solid h-11 flex-1"><Check size={13}/> Save adjustments</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ num, label, value, accent }) {
  return (
    <div data-testid={`period-kpi-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono ink text-2xl font-semibold ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
    </div>
  );
}
